import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ArrowDown } from "lucide-react";
import { SITE } from "@/config";
import { useMotion } from "@/motion/MotionProvider";
import { useFloatingAnimation, useMouseParallax, useMagneticButton, usePremiumButton, useTextReveal } from "@/motion/hooks";
import { DUR, STAGGER, FX } from "@/motion/tokens";

export default function Hero() {
    const { enabled } = useMotion();
    const stageRef = useRef(null);   // camera-drift wrapper (bg composition)
    const bgRef = useRef(null);      // gradient field — independent slow shift
    const copyRef = useRef(null);    // text container for the entrance timeline
    const cueRef = useRef(null);     // scroll cue — drifts, dies on scroll

    const blobA = useFloatingAnimation({ amplitude: [6, 18] }); // glow blobs — organic drift
    const blobB = useFloatingAnimation({ amplitude: [8, 26] });
    const glowField = useMouseParallax({ move: 10, rotate: 1.5 }); // nearest: glow
    const bgDepth = useMouseParallax({ move: 4, invert: true, lag: 0.9 }); // farthest: gradient, counter-moves
    const copyDepth = useMouseParallax({ move: 6, lag: 0.8 }); // mid: headline block
    const cta = useMagneticButton();
    const ctaPremium = usePremiumButton("fill");
    const titleReveal = useTextReveal("chars", { onLoad: true, delay: 0.3 });
    const subReveal = useTextReveal("words", { onLoad: true, delay: 0.55 });

    // Entrance: label → headline → sub → CTA. opacity + blur + translateY, staggered.
    useEffect(() => {
        if (!enabled || !copyRef.current) return;
        const ctx = gsap.context(() => {
            const label = copyRef.current.querySelector("[data-hero-label]");
            const cta = copyRef.current.querySelector("[data-hero-cta]");
            const vars = (delay) => ({
                opacity: 1, y: 0, filter: "blur(0px)",
                duration: DUR.slow * 1.4, delay, clearProps: "filter",
            });
            const from = { opacity: 0, y: 26, filter: `blur(${FX.blur}px)` };
            gsap.fromTo(label, from, vars(0.1));   // 1. eyebrow
            /* 2. name (chars, 0.3) and 3. sub (words, 0.55) run via useTextReveal */
            gsap.fromTo(cta, from, vars(0.9));     // 4. CTA — always last
        }, copyRef);
        return () => ctx.revert();
    }, [enabled]);

    // Camera drift: extremely slow breathe on the stage. Transform only.
    useEffect(() => {
        if (!enabled || !stageRef.current) return;
        const ctx = gsap.context(() => {
            gsap.to(stageRef.current, {
                scale: 1.035,
                xPercent: 0.4,
                yPercent: -0.4,
                duration: 34,
                repeat: -1,
                yoyo: true,
                ease: "sine.inOut",
            });
        }, stageRef);
        return () => ctx.revert();
    }, [enabled]);

    // Scroll cue: bespoke drift; fades away as soon as the journey begins.
    useEffect(() => {
        if (!enabled || !cueRef.current) return;
        const ctx = gsap.context(() => {
            gsap.to(cueRef.current.querySelector("[data-cue-arrow]"), {
                y: 6, duration: 1.6, repeat: -1, yoyo: true, ease: "sine.inOut",
            });
            gsap.to(cueRef.current, {
                opacity: 0,
                scrollTrigger: { trigger: document.body, start: "80 top", end: "260 top", scrub: true },
            });
        }, cueRef);
        return () => ctx.revert();
    }, [enabled]);

    // Gradient field shift: independent, slower than the camera, opposite drift.
    useEffect(() => {
        if (!enabled || !bgRef.current) return;
        const ctx = gsap.context(() => {
            gsap.to(bgRef.current, {
                xPercent: -2.5,
                yPercent: 1.5,
                scale: 1.06,
                rotate: 0.6,
                duration: 46,
                repeat: -1,
                yoyo: true,
                ease: "sine.inOut",
            });
        }, bgRef);
        return () => ctx.revert();
    }, [enabled]);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative flex min-h-[100svh] w-full items-center overflow-hidden"
        >
            <div ref={stageRef} className="absolute inset-0 will-change-transform">
                {/* Cinematic dark treatment — no photography, warm gold glow */}
                <div className="absolute inset-0 bg-[#030303]" />

                {/* Gradient field — moves independently of the camera */}
                <div ref={bgRef} className="absolute -inset-[6%] will-change-transform">
                    <div ref={bgDepth} className="absolute inset-0 will-change-transform">
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,rgba(198,167,107,0.10),transparent_60%)]" />
                    </div>
                </div>

                {/* Blurred glow blobs — drift naturally, lean toward the pointer */}
                <div ref={glowField} className="pointer-events-none absolute inset-0 will-change-transform">
                    <div
                        ref={blobA}
                        className="absolute left-[18%] top-[22%] h-[38vmin] w-[38vmin] rounded-full bg-[radial-gradient(circle,rgba(198,167,107,0.07),transparent_70%)] blur-3xl will-change-transform"
                    />
                    <div
                        ref={blobB}
                        className="absolute right-[14%] bottom-[18%] h-[46vmin] w-[46vmin] rounded-full bg-[radial-gradient(circle,rgba(198,167,107,0.05),transparent_70%)] blur-3xl will-change-transform"
                    />
                </div>

                <div className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 gold-line opacity-40" />
            </div>

            <div ref={copyRef} className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-10 pt-28 pb-20 text-center">
                <div ref={copyDepth} className="will-change-transform">
                <p
                    data-hero-label
                    className="text-[11px] md:text-xs uppercase tracking-luxe text-[color:var(--gold)]"
                >
                    AI Visual Creator &amp; Video Editor
                </p>

                <h1
                    ref={titleReveal}
                    data-testid="hero-title"
                    className="font-display mt-6 text-6xl sm:text-7xl md:text-[7.5rem] leading-[0.95] tracking-tight"
                >
                    Nikhil Joshi
                </h1>

                <p
                    ref={subReveal}
                    className="mx-auto mt-8 max-w-2xl font-display italic text-xl md:text-2xl text-[color:var(--fg)]/75"
                >
                    {SITE.positioning}
                </p>

                <div data-hero-cta className="mt-12 flex justify-center">
                    <span ref={cta} className="inline-block will-change-transform">
                        <a
                            ref={ctaPremium}
                            href="#contact"
                            data-testid="hero-cta-work"
                            className="group inline-flex items-center gap-3 border border-[color:var(--gold)] px-10 py-4 text-xs uppercase tracking-[0.24em] text-[color:var(--gold)] will-change-transform"
                        >
                            Work With Me
                        </a>
                    </span>
                </div>
                </div>
            </div>

            <div ref={cueRef} className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[color:var(--muted)]">
                <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                <ArrowDown size={14} data-cue-arrow />
            </div>
        </section>
    );
}
